import type { ACSServer, AccessEvent, Alert, AlertRule, Subscriber, SubscriberONT, ApiError, CPEDevice, LoginResp, Me, NetLink, OLTEvent, NetMapResponse, NetNode, ONT, ONTDetailResponse, ONTEvent, PanelUser, Invite, Role, Overview, OLTResources, OLTPortStat } from "./types";

const TOKEN_KEY = "nocops.access";

export function getAccessToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}
export function setAccessToken(t: string | null) {
  if (t === null) localStorage.removeItem(TOKEN_KEY);
  else localStorage.setItem(TOKEN_KEY, t);
}

export class ApiCallError extends Error {
  status: number;
  constructor(status: number, msg: string) {
    super(msg);
    this.status = status;
  }
}

async function refresh(): Promise<string | null> {
  const r = localStorage.getItem("nocops.refresh");
  if (!r) return null;
  const res = await fetch("/auth/refresh", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ refresh_token: r }),
  });
  if (!res.ok) {
    setAccessToken(null);
    localStorage.removeItem("nocops.refresh");
    return null;
  }
  const data = (await res.json()) as LoginResp;
  setAccessToken(data.access_token);
  localStorage.setItem("nocops.refresh", data.refresh_token);
  return data.access_token;
}

export async function api<T>(
  path: string,
  init: RequestInit = {},
  retried = false,
): Promise<T> {
  const headers = new Headers(init.headers ?? {});
  headers.set("Content-Type", "application/json");

  const tok = getAccessToken();
  if (tok) headers.set("Authorization", `Bearer ${tok}`);

  const res = await fetch(path, { ...init, headers });

  if (res.status === 401 && !retried) {
    const newTok = await refresh();
    if (newTok) return api<T>(path, init, true);
  }

  if (!res.ok) {
    let msg = res.statusText;
    try {
      const body = (await res.json()) as ApiError;
      if (body?.error) msg = body.error;
    } catch {
      // fall through
    }
    throw new ApiCallError(res.status, msg);
  }

  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

// ----- Auth-specific calls -----

export async function login(email: string, password: string): Promise<LoginResp> {
  const res = await fetch("/auth/login", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, password }),
  });
  if (!res.ok) {
    let msg = "login failed";
    try {
      const body = (await res.json()) as ApiError;
      if (body?.error) msg = body.error;
    } catch {}
    throw new ApiCallError(res.status, msg);
  }
  const data = (await res.json()) as LoginResp;
  setAccessToken(data.access_token);
  localStorage.setItem("nocops.refresh", data.refresh_token);
  return data;
}

export async function me(): Promise<Me | null> {
  if (!getAccessToken()) return null;
  try {
    return await api<Me>("/auth/me");
  } catch {
    return null;
  }
}

export async function logout(): Promise<void> {
  const r = localStorage.getItem("nocops.refresh");
  if (r) {
    try {
      await api("/auth/logout", { method: "POST", body: JSON.stringify({ refresh_token: r }) });
    } catch {}
  }
  setAccessToken(null);
  localStorage.removeItem("nocops.refresh");
}
// ----- ONT calls -----

export function listONTs(params: { q?: string; state?: string; limit?: number; oltId?: number | string; pon?: string } = {}) {
  const qs = new URLSearchParams();
  if (params.q) qs.set("q", params.q);
  if (params.state) qs.set("state", params.state);
  if (params.oltId !== undefined) qs.set("olt_id", String(params.oltId));
  if (params.pon) qs.set("pon", params.pon);
  qs.set("limit", String(params.limit ?? 500));
  return api<{ onts: ONT[]; count: number }>(`/api/v1/onts?${qs}`);
}

export function getONT(id: string | number) {
  return api<ONTDetailResponse>(`/api/v1/onts/${id}`);
}

export function ontEvents(id: string | number, limit = 100, since?: string) {
  const qs = new URLSearchParams({ limit: String(limit) });
  if (since) qs.set("since", since);
  return api<{ items: ONTEvent[] }>(`/api/v1/onts/${id}/events?${qs}`);
}

/** Partial: omit a key to leave it untouched, send null to clear it. */
export function updateONT(id: string | number, body: { notes?: string | null; cpe_device_id?: number | null }) {
  return api<{ ok: true }>(`/api/v1/onts/${id}`, { method: "PATCH", body: JSON.stringify(body) });
}

export function listCPEDevices(q: string, limit = 50) {
  const qs = new URLSearchParams({ limit: String(limit) });
  if (q) qs.set("q", q);
  return api<{ items: CPEDevice[] }>(`/api/v1/cpe-devices?${qs}`);
}

// ----- Alert rules -----

export type AlertRulePatch = Partial<
  Pick<
    AlertRule,
    | "name" | "metric" | "condition" | "threshold" | "window_s"
    | "hysteresis" | "consecutive" | "severity" | "cooldown_s" | "enabled"
  >
>;

export function listAlertRules() {
  return api<{ rules: AlertRule[] }>("/api/v1/alert-rules");
}

export function updateAlertRule(id: number, body: AlertRulePatch) {
  return api<{ ok: true }>(`/api/v1/alert-rules/${id}`, { method: "PATCH", body: JSON.stringify(body) });
}

export function deleteAlertRule(id: number) {
  return api<{ ok: true; id: number }>(`/api/v1/alert-rules/${id}`, { method: "DELETE" });
}

// ----- Network link map -----

export function getNetMap(peers = false) {
  return api<NetMapResponse>(`/api/v1/netmap${peers ? "?peers=1" : ""}`);
}

export function listNetNodes() {
  return api<{ items: NetNode[] }>("/api/v1/net-nodes");
}

export function listNetLinks() {
  return api<{ items: NetLink[] }>("/api/v1/net-links");
}

export type NetLinkForm = {
  source_node: string;
  target_node: string;
  source_port_id?: number | null;
  target_port_id?: number | null;
  label?: string | null;
};

export function createNetLink(body: NetLinkForm) {
  return api<{ id: number }>("/api/v1/net-links", { method: "POST", body: JSON.stringify(body) });
}

export function updateNetLink(id: number, body: Partial<NetLinkForm>) {
  return api<{ ok: true }>(`/api/v1/net-links/${id}`, { method: "PATCH", body: JSON.stringify(body) });
}

export function deleteNetLink(id: number) {
  return api<{ ok: true }>(`/api/v1/net-links/${id}`, { method: "DELETE" });
}

// ----- OLT onboarding / maintenance (admin only) -----

export type OLTForm = {
  name: string;
  mgmt_ip: string;
  mgmt_port?: number;
  mgmt_protocol?: string;
  mgmt_username: string;
  mgmt_password?: string;
  enable_password?: string;
  driver: string;
  model?: string;
  vendor?: string;
  location?: string;
};

export function createOLT(body: OLTForm) {
  return api<{ id: number }>("/api/v1/olts", { method: "POST", body: JSON.stringify(body) });
}

// PATCH is partial: omit a password to keep the stored one.
export function updateOLT(id: number, body: Partial<OLTForm>) {
  return api<{ id: number }>(`/api/v1/olts/${id}`, { method: "PATCH", body: JSON.stringify(body) });
}

// ----- ACS (GenieACS) servers -----

export type ACSServerForm = {
  name: string;
  base_url: string;
  username?: string;
  password?: string;
  enabled?: boolean;
  notes?: string;
};

export function listACSServers() {
  return api<{ acs_servers: ACSServer[] }>("/api/v1/acs-servers");
}

export function createACSServer(body: ACSServerForm) {
  return api<{ id: number }>("/api/v1/acs-servers", { method: "POST", body: JSON.stringify(body) });
}

// Omit/blank password to keep the stored one.
export function updateACSServer(id: number, body: Partial<ACSServerForm>) {
  return api<{ id: number }>(`/api/v1/acs-servers/${id}`, { method: "PATCH", body: JSON.stringify(body) });
}

export function deleteACSServer(id: number) {
  return api<{ cpe_devices_deleted: number }>(`/api/v1/acs-servers/${id}`, { method: "DELETE" });
}

export function testACSServer(id: number) {
  return api<{ ok: true; status: string; devices: number; info: string }>(`/api/v1/acs-servers/${id}/test`, { method: "POST" });
}

export function syncACSServer(id: number) {
  return api<{ ok: true; devices: number }>(`/api/v1/acs-servers/${id}/sync`, { method: "POST" });
}

// ----- Map node positions (The Dude style: drag it, it stays) -----

export type NetPosition = { node_key: string; x: number; y: number };

export function listNetPositions() {
  return api<{ positions: NetPosition[] }>("/api/v1/net-positions");
}

/** Upsert in one request so a multi-node drag is one round trip. Admin only. */
export function saveNetPositions(positions: NetPosition[]) {
  return api<{ ok: true }>("/api/v1/net-positions", { method: "PUT", body: JSON.stringify({ positions }) });
}

/** "Auto arrange": drop every saved position. Admin only. */
export function clearNetPositions() {
  return api<{ ok: true }>("/api/v1/net-positions", { method: "DELETE" });
}

// ----- LibreNMS per-link graph (proxied; the token stays server-side) -----

export type GraphWindow = "1h" | "6h" | "24h" | "7d" | "30d";
/** Render widths the proxy whitelists (height is 1/4 of the width). */
export type GraphWidth = 800 | 1200 | 1600;

/**
 * The SVG needs the same Authorization header as every other call, which an
 * <img src> cannot send — so fetch it here and let the caller render a blob URL.
 */
async function fetchSVG(path: string, retried = false): Promise<Blob> {
  const tok = getAccessToken();
  const res = await fetch(path, { headers: tok ? { Authorization: `Bearer ${tok}` } : {} });
  if (res.status === 401 && !retried) {
    const newTok = await refresh();
    if (newTok) return fetchSVG(path, true);
  }
  if (!res.ok) {
    let msg = res.statusText;
    try {
      const body = (await res.json()) as ApiError;
      if (body?.error) msg = body.error;
    } catch {
      // upstream may not answer in JSON
    }
    throw new ApiCallError(res.status, msg);
  }
  return res.blob();
}

export function linkGraph(id: number, side: "source" | "target", window: GraphWindow, width: GraphWidth = 800): Promise<Blob> {
  return fetchSVG(`/api/v1/net-links/${id}/graph?side=${side}&type=port_bits&window=${window}&width=${width}`);
}

/** Any mirrored port, by the ids /graph-targets hands out. */
export function portGraph(deviceId: number, portId: number, window: GraphWindow, width: GraphWidth = 800): Promise<Blob> {
  return fetchSVG(`/api/v1/graph?device_id=${deviceId}&port_id=${portId}&type=port_bits&window=${window}&width=${width}`);
}

export type GraphTargetPort = {
  port_id: number;
  ifname: string;
  ifalias: string | null;
  speed_bps: number | null;
  oper_status: string | null;
  rx_bps: number | null;
  tx_bps: number | null;
};

export type GraphTargetDevice = {
  device_id: number;
  name: string;
  hostname: string;
  ports: GraphTargetPort[];
};

export function graphTargets(q = "") {
  const qs = q ? `?q=${encodeURIComponent(q)}` : "";
  return api<{ devices: GraphTargetDevice[]; capped: boolean; librenms_configured: boolean }>(
    `/api/v1/graph-targets${qs}`,
  );
}

export function createAlertRule(body: AlertRulePatch & { name: string; metric: string; condition: string }) {
  return api<AlertRule>("/api/v1/alert-rules", { method: "POST", body: JSON.stringify(body) });
}

// ----- Users and invites (admin) -----

export function listUsers() {
  return api<{ users: PanelUser[] }>("/api/v1/users");
}

export function updateUser(id: number, body: { role?: Role; disabled?: boolean }) {
  return api<{ ok: true }>(`/api/v1/users/${id}`, { method: "PATCH", body: JSON.stringify(body) });
}

export function disableUser(id: number) {
  return api<{ ok: true }>(`/api/v1/users/${id}`, { method: "DELETE" });
}

export function listInvites() {
  return api<{ invites: Invite[] }>("/api/v1/invites");
}

/** The response carries the one-time link (or bare token when no base URL is set). */
export function revokeSessions(id: number) {
  return api<{ ok: true; revoked: number }>(`/api/v1/users/${id}/sessions/revoke`, { method: "POST" });
}

export function accessAudit(q: { category?: string; user?: number; limit?: number } = {}) {
  const p = new URLSearchParams();
  if (q.category) p.set("category", q.category);
  if (q.user) p.set("user", String(q.user));
  if (q.limit) p.set("limit", String(q.limit));
  return api<{ events: AccessEvent[] }>(`/api/v1/audit/access?${p}`);
}

export function createInvite(body: { email: string; role: Role; ttl_days?: number }) {
  return api<{ id: number; email: string; role: Role; expires_at: string; invite_url?: string; token?: string }>(
    "/api/v1/invites",
    { method: "POST", body: JSON.stringify(body) },
  );
}

export function revokeInvite(id: number) {
  return api<{ ok: true }>(`/api/v1/invites/${id}`, { method: "DELETE" });
}

export function changePassword(current_password: string, new_password: string) {
  return api<{ ok: true }>("/auth/change-password", {
    method: "POST",
    body: JSON.stringify({ current_password, new_password }),
  });
}

/** Invite lookup and accept are PUBLIC: the person has no session yet. */
export async function lookupInvite(token: string) {
  const res = await fetch(`/auth/invite?token=${encodeURIComponent(token)}`);
  if (!res.ok) throw new ApiCallError(res.status, ((await res.json().catch(() => ({}))) as ApiError)?.error ?? "invite not found");
  return (await res.json()) as { email: string; role: Role; valid: boolean };
}

export async function acceptInvite(token: string, password: string): Promise<LoginResp> {
  const res = await fetch("/auth/accept-invite", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ token, password }),
  });
  if (!res.ok) {
    throw new ApiCallError(res.status, ((await res.json().catch(() => ({}))) as ApiError)?.error ?? "could not accept the invite");
  }
  const data = (await res.json()) as LoginResp;
  setAccessToken(data.access_token);
  localStorage.setItem("nocops.refresh", data.refresh_token);
  return data;
}

/** One poll shared by the overview page, the topbar LIVE beacon and the bell. */
export const OVERVIEW_REFRESH_MS = 20_000;

export function getOverview() {
  return api<Overview>("/api/v1/overview");
}

export function getOLTResources(id: string | number) {
  return api<OLTResources>(`/api/v1/olts/${id}/resources`);
}

export function getOLTEvents(id: string | number, days = 7) {
  return api<{ items: OLTEvent[]; days: number }>(`/api/v1/olts/${id}/events?days=${days}`);
}

export function getOLTPorts(id: string | number) {
  return api<{ ports: OLTPortStat[] }>(`/api/v1/olts/${id}/ports`);
}

// ----- Graph shares -----

export type GraphShare = {
  id: number;
  label: string;
  ports: string[];
  expires_at: string | null;
  revoked_at: string | null;
  view_count: number;
  last_viewed_at: string | null;
  created_at: string;
  created_by_email?: string | null;
  status: "active" | "expired" | "revoked";
};

export function listGraphShares() {
  return api<{ shares: GraphShare[] }>("/api/v1/graph-shares");
}

/** The URL comes back ONCE: the server stores only a hash of the token. */
export function createGraphShare(body: { label: string; days: number; ports: { port_id: number; label: string }[] }) {
  return api<{ id: number; label: string; url?: string; token?: string; expires_at: string | null }>(
    "/api/v1/graph-shares",
    { method: "POST", body: JSON.stringify(body) },
  );
}

export function revokeGraphShare(id: number) {
  return api<{ ok: true }>(`/api/v1/graph-shares/${id}`, { method: "DELETE" });
}

/** Moves the expiry to `days` from now. */
export function extendGraphShare(id: number, days: number) {
  return api<{ ok: true; expires_at: string }>(`/api/v1/graph-shares/${id}`, { method: "PATCH", body: JSON.stringify({ days }) });
}

// ----- Settings summary: presence and audit only, never a secret -----

export type Changed = { at: string; by: string } | null;
export type SettingsSummary = {
  security: { vault_configured: boolean; algorithm: string; olt_credentials: number; olts: number; acs_credentials: number };
  olts: {
    id: number; name: string; protocol: string; port: number; has_password: boolean; has_enable_password: boolean;
    status: string; last_polled_at: string | null; last_error: string | null; changed: Changed; username?: string;
  }[];
  integrations: {
    acs_servers: number;
    librenms: { configured: boolean; host: string };
    smtp: { configured: boolean; host: string; auth: boolean; from: string; recipients: number };
    telegram: { configured: boolean; token: boolean; chat: boolean };
    whmcs: { configured: boolean; host: string };
  };
  monitoring: { olt_poll_s: number; snmp_poll_s: number; sync_s: number; whmcs_sync_s: number; alert_eval_s: number; util_high_pct: number; util_crit_pct: number };
  sharing: { active: number; total: number };
  general: { public_base_url: string; olt_timezone: string; invite_ttl_s: number };
  rule_changes: Record<string, { at: string; by: string }>;
  recent: { at: string; action: string; actor_email: string; target: string; target_id?: number }[];
};

export function settingsSummary() {
  return api<SettingsSummary>("/api/v1/settings/summary");
}

/** Public: the token in the path is the only credential, so no auth header. */
export async function shareMeta(token: string) {
  const res = await fetch(`/s/${encodeURIComponent(token)}/meta`);
  if (!res.ok) {
    throw new ApiCallError(res.status, ((await res.json().catch(() => ({}))) as ApiError)?.error ?? "this link is not valid");
  }
  return (await res.json()) as {
    label: string;
    graphs: { id: number; label: string }[];
    expires_at: string | null;
  };
}

export async function shareGraph(token: string, id: number, window: GraphWindow, width: GraphWidth = 800): Promise<Blob> {
  const res = await fetch(`/s/${encodeURIComponent(token)}/graph/${id}?window=${window}&width=${width}`);
  if (!res.ok) throw new ApiCallError(res.status, "graph unavailable");
  return res.blob();
}

/** Graphs assigned to the signed-in account (used by port-viewer logins). */
export function myGraphs() {
  return api<{ graphs: { id: number; label: string }[] }>("/api/v1/my/graphs");
}

export function myGraph(id: number, window: GraphWindow, width: GraphWidth = 800): Promise<Blob> {
  return fetchSVG(`/api/v1/my/graphs/${id}?window=${window}&width=${width}`);
}

export function userGraphPorts(userID: number) {
  return api<{ ports: { id: number; librenms_port_id: number; label: string }[] }>(`/api/v1/users/${userID}/graph-ports`);
}

export function setUserGraphPorts(userID: number, ports: { port_id: number; label: string }[]) {
  return api<{ ok: true }>(`/api/v1/users/${userID}/graph-ports`, { method: "PUT", body: JSON.stringify({ ports }) });
}

// ----- CPE WAN (read-only) -----

export type WANConnection = {
  path: string;
  name: string;
  /** PPPoE or IPoE, from the TR-069 object type. */
  mode: "PPPoE" | "IPoE";
  addressing: string;
  connection_type: string;
  enabled: boolean | null;
  status: string;
  vlan: number | null;
  external_ip: string;
  /** PPPoE user; the password is never read from the device. */
  username: string;
  nat: boolean | null;
  uptime_s: number | null;
};

export type ONTWAN = {
  linked: boolean;
  link_source?: string;
  acs_server?: string;
  device_id?: string;
  model?: string | null;
  last_inform?: string | null;
  connections: WANConnection[];
};

export function ontWAN(id: string | number) {
  return api<ONTWAN>(`/api/v1/onts/${id}/wan`);
}

/** Superadmin only: set another account's password without the old one. */
export function resetUserPassword(id: number, new_password: string) {
  return api<{ ok: true; sessions_revoked: boolean }>(`/api/v1/users/${id}/password`, {
    method: "PUT",
    body: JSON.stringify({ new_password }),
  });
}

// ----- Subscribers: WHMCS services joined to the network -----

export function listSubscribers() {
  return api<{ subscribers: Subscriber[]; count: number; generated_at: string }>("/api/v1/subscribers");
}

export type SubscriberDetail = {
  subscriber: Subscriber;
  alerts: Alert[];
  events: ONTEvent[];
  links: { at: string; action: "subscriber.link" | "subscriber.unlink"; ont_id: number; serial: string; by: string }[];
};

export function getSubscriber(id: number) {
  return api<SubscriberDetail>(`/api/v1/subscribers/${id}`);
}

/** An ONT offered for linking; suggestions carry why, and who already has it. */
export type ONTCandidate = Omit<SubscriberONT, "link"> & {
  confidence: "strong" | "possible" | "";
  reason: "cpe_serial" | "name" | "";
  linked_to?: { id: number; full_name: string };
};

export function subscriberCandidates(id: number, q = "") {
  return api<{ suggested?: ONTCandidate[]; results?: ONTCandidate[] }>(
    `/api/v1/subscribers/${id}/candidates${q ? `?q=${encodeURIComponent(q)}` : ""}`,
  );
}

export function linkSubscriberONT(id: number, ontId: number) {
  return api<{ ok: true }>(`/api/v1/subscribers/${id}/onts`, { method: "POST", body: JSON.stringify({ ont_id: ontId }) });
}

export function unlinkSubscriberONT(id: number, ontId: number) {
  return api<{ ok: true }>(`/api/v1/subscribers/${id}/onts/${ontId}`, { method: "DELETE" });
}

// ----- BRAS: routers, internet probes, ping / trace from a router -----

export type BRASRouter = {
  id: number; name: string; kind: "routeros" | "linux"; host: string; port: number; enabled: boolean;
  /** RouterOS API port (8728; 8729 with api_tls); 0 = SSH exec. */
  api_port: number; api_tls: boolean;
  status: "unknown" | "up" | "down"; identity: string | null; board: string | null; version: string | null;
  uptime_s: number | null; cpu_pct: number | null; mem_used_b: number | null; mem_total_b: number | null;
  pppoe_sessions: number | null; last_polled_at: string | null; last_ok_at: string | null; last_error: string | null;
  fail_count: number; notes: string | null; has_password: boolean;
  /** Admins only. */
  username?: string;
  pppoe_series?: { ts: string; n: number }[] | null;
  pppoe_delta_1h?: number;
};
export type ProbeTarget = {
  id: number; name: string; address: string; enabled: boolean; category: string;
  /** Empty / null = every BRAS. */
  router_ids: number[] | null;
  latency_warn_ms: number | null; loss_warn_pct: number | null;
};
export type ProbeSample = {
  ts: string; router_id: number; target_id: number; sent: number; received: number;
  loss_pct: number | null; rtt_min: number | null; rtt_avg: number | null; rtt_max: number | null; error: string | null;
};
/** ip: the server a hostname target resolved to for this probe. */
export type ProbePoint = { ts: string; loss: number | null; avg: number | null; error?: boolean; ip?: string };
/** One server a hostname target resolved to over the window; name is reverse DNS. */
export type ServerStat = { ip: string; share: number; median_ms: number | null; name?: string };
export type Hop = { n: number; address: string; loss_pct: number; sent: number; last_ms: number | null; avg_ms: number | null; best_ms: number | null; worst_ms: number | null };
export type PingResult = { sent: number; received: number; loss_pct: number; min_ms: number | null; avg_ms: number | null; max_ms: number | null };
/** A manual or automatic diagnostic run. error_kind: api | timeout | unreachable | incomplete. */
export type ProbeTrace = {
  id: number; ts: string; router_id: number; target: string; hops: Hop[]; source: "manual" | "auto"; kind: "mtr" | "ping";
  error: string | null; error_kind: "api" | "timeout" | "unreachable" | "incomplete" | null; result?: PingResult;
};
export type PairState = "healthy" | "degraded" | "loss" | "unreachable" | "unknown";
export type Pair = {
  state: PairState; reason: string; at: string | null;
  latest_ms: number | null; loss_pct: number | null; median_ms: number | null; baseline_ms: number | null;
  window_loss: number | null; window_avg: number | null; window_min: number | null; window_max: number | null; jitter_ms: number | null;
  error?: string;
  resolved_ip?: string;
  ips?: ServerStat[];
};
export type Finding = { case: "shared" | "bras" | "target" | "path"; severity: "warn" | "critical"; router_ids: number[]; target_ids: number[]; text: string };
export type ProbePolicy = { loss_warn_pct: number; loss_crit_pct: number; dev_pct: number; dev_min_ms: number; ppp_drop_pct: number; ppp_drop_min: number };
export type BRASOverview = {
  routers: BRASRouter[]; targets: ProbeTarget[];
  /** "routerId:targetId" -> the last 3 hours of probes. */
  series: Record<string, ProbePoint[]>;
  analysis: { pairs: Record<string, Pair>; findings: Finding[] | null };
  policy: ProbePolicy;
  incidents: Alert[];
  diagnostics: ProbeTrace[]; auto_traces: ProbeTrace[];
  interval_s: number; last_probe_at: string | null; generated_at: string;
};
export type RouterForm = { name?: string; kind?: "routeros" | "linux"; host?: string; port?: number; api_port?: number; api_tls?: boolean; username?: string; password?: string; enabled?: boolean; notes?: string };
export type TargetForm = { name?: string; address?: string; enabled?: boolean; category?: string; router_ids?: number[]; latency_warn_ms?: number; loss_warn_pct?: number; clear_thresholds?: boolean };
export type CustomerPing =
  | { session: true; router: string; login: string; address: string; result: PingResult }
  | { session: false; logins: string[]; checked: { router: string; error: string | null }[]; last_cpe_ip: string | null };

export const brasOverview = () => api<BRASOverview>("/api/v1/bras");
export const createRouter = (b: RouterForm) => api<{ id: number }>("/api/v1/bras", { method: "POST", body: JSON.stringify(b) });
export const updateRouter = (id: number, b: RouterForm) => api<{ id: number }>(`/api/v1/bras/${id}`, { method: "PATCH", body: JSON.stringify(b) });
export const deleteRouter = (id: number) => api<{ ok: true }>(`/api/v1/bras/${id}`, { method: "DELETE" });
export const testRouter = (id: number) =>
  api<{ ok: true; identity: string; board: string; version: string; pppoe_sessions: number | null }>(`/api/v1/bras/${id}/test`, { method: "POST" });
export const routerPing = (id: number, target: string, count = 10) =>
  api<{ router: string; target: string; result: PingResult }>(`/api/v1/bras/${id}/ping`, { method: "POST", body: JSON.stringify({ target, count }) });
export const routerTrace = (id: number, target: string) =>
  api<ProbeTrace>(`/api/v1/bras/${id}/trace`, { method: "POST", body: JSON.stringify({ target }) });
export const saveProbeTarget = (b: TargetForm, id?: number) =>
  api<ProbeTarget>(id ? `/api/v1/probe-targets/${id}` : "/api/v1/probe-targets", { method: id ? "PATCH" : "POST", body: JSON.stringify(b) });
export const deleteProbeTarget = (id: number) => api<{ ok: true }>(`/api/v1/probe-targets/${id}`, { method: "DELETE" });
export const saveProbePolicy = (p: ProbePolicy) => api<ProbePolicy>("/api/v1/probe-policy", { method: "PUT", body: JSON.stringify(p) });
export const routerHistory = (id: number, window: string) =>
  api<{ samples: { ts: string; pppoe: number | null; cpu_pct: number | null; mem_pct: number | null }[] }>(`/api/v1/bras/${id}/history?window=${window}`);
export const targetHistory = (target: number, window: string) => api<{ samples: ProbeSample[] }>(`/api/v1/bras/samples?target=${target}&window=${window}`);
export const customerPing = (kind: "subscribers" | "onts", id: number) => api<CustomerPing>(`/api/v1/${kind}/${id}/ping`, { method: "POST" });
